import { Controller } from "@hotwired/stimulus";
import TomSelect from "tom-select";

export default class extends Controller {
  static values = {
    optionFetchUrl: String,
    tagClasses: { type: String, default: "inline-flex items-center gap-1 bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm border border-purple-200 hover:bg-purple-200 transition-colors" },
    removeClasses: { type: String, default: "ml-1 text-purple-600 hover:text-purple-800 hover:bg-purple-300 rounded-full px-1 pb-1 transition-colors focus:outline-none focus:ring-1 focus:ring-purple-400" },
    selectedId: { type: String, default: "[]" },
    containerId: { type: String, default: "showlist" },
    showHeader: { type: Boolean, default: false },
    placeholder: { type: String, default: "Search and select org/brands..." },
  }

  connect() {
    this.tagContainer = this.element.parentElement.parentElement.querySelector(`#${this.containerIdValue}`)

    if (!this.tagContainer) {
      console.error(`Multiple select container with ID '${this.containerIdValue}' not found`)
      return
    }

    this.selectedItems = new Map()

    this.step = 1
    this.initializeTomSelect();
  }

  initializeTomSelect() {
    const organizationSearchUrl = this.optionFetchUrlValue
    this.tomSelect = new TomSelect(this.element, {
      plugins: {
        virtual_scroll: true,
      },
      firstUrl: query => query ? `${organizationSearchUrl}?q=${encodeURIComponent(query)}` : organizationSearchUrl,
      valueField: "value",
      labelField: "name",
      searchField: ["name"],
      optgroupField: "class",
      optgroupLabelField: "label",
      optgroupValueField: "value",
      preload: true,
      persist: false,
      createOnBlur: false,
      placeholder: this.placeholderValue,
      hideSelected: true,
      maxOptions: null,
      items: JSON.parse(this.selectedIdValue),
      load: this.loadOptions.bind(this),
      shouldLoadMore: this.shouldLoad.bind(this),
      onItemAdd: this.handleItemAdd.bind(this),
      onItemRemove: this.handleItemRemove.bind(this),
      onType: this.handleType.bind(this),
      render: {
        optgroup_header: (data) =>
          `<div class="optgroup-header !text-gray-400 font-semibold">${data.label}</div>`,
      }
    });

    this.tomSelect.on('change', () => {
      this.clearSearch();
      if (this.element.id.includes('organization')) {
        this.element.dispatchEvent(new CustomEvent('organizationChanged', {
          detail: { selectedIds: this.tomSelect.items }
        }));
      }
    });

    this.updateSelected();
  }

  shouldLoad() {
    const dropdown_content = this.tomSelect.dropdown_content
    const scroll_percent = dropdown_content.clientHeight / (dropdown_content.scrollHeight - dropdown_content.scrollTop);

    if(scroll_percent > 0.9 && this.step != null){
      this.scrollHeight = dropdown_content.scrollHeight
      this.tomSelect.load()
    }

  }

  loadOptions(query, callback) {
    const alreadyParamAdded = this.tomSelect.getUrl(query).includes('?')

    const url = this.tomSelect.getUrl(query) + `${query || alreadyParamAdded ? '&' : '?'}page=${this.step && !query ? this.step : query ? '1' : ''}`
    fetch(url)
      .then((response) => response.json())
      .then((result) => {
        const data = result.data;
        let options = []
        let newOptions = []

        if (this.showHeaderValue) {
          this.processAndAddData(data);
          options = data.flatMap((group) => group.items);
        } else {
          options = data
        }

        if(this.step > 1) {
          newOptions = [...options, ...Object.values(this.tomSelect.options)]
        } else {
          newOptions = [...options]
        }

        this.step = result.next_page


        newOptions.sort((a, b) => {
          return a.name.localeCompare(b.name);
        });

        callback(newOptions);

        this.tomSelect.dropdown_content.scrollTop = this.scrollHeight * 0.8;
      })
      .catch(() => callback([]));
  }

  clearSearch() {
    const searchInput = this.element.parentElement.querySelector('.ts-control input[type="text"]');

    if (searchInput) {
      searchInput.value = '';
      this.tomSelect.refreshOptions();
    }
  }

  handleType(str) {
    this.tomSelect.load(str)
  }

  updateSelected() {
    Object.values(this.tomSelect.options).forEach(el => {
      if(this.tomSelect.items.includes(el.value)) {
        this.createTag(el.value, el.name)
      }
    })
  }

  handleItemAdd(value, item) {
    // Hide the item in dropdown
    item.classList.add('!hidden')

    // Store the selected item
    this.selectedItems.set(value, {
      value: value,
      text: item.textContent.trim(),
      element: item
    })

    this.createTag(value, item.textContent.trim())
  }

  handleItemRemove(value) {
    // This will be called when we programmatically remove items
    const itemData = this.selectedItems.get(value)
    if (itemData && itemData.element) {
      itemData.element.classList.remove('!hidden')
    }
    this.selectedItems.delete(value)
  }

  createTag(value, text) {
    if (!this.tagContainer) {
      console.error('Tag container not found, cannot create tag')
      return
    }

    // Check if tag already exists to prevent duplicates
    const existingTag = this.tagContainer.querySelector(`[data-value="${value}"]`)
    if (existingTag) {
      return // Tag already exists, don't create duplicate
    }

    const tag = document.createElement('div')
    tag.className = this.tagClassesValue
    tag.dataset.value = value

    // Tag text
    const tagText = document.createElement('span')
    tagText.textContent = text
    tagText.className = 'font-medium truncate flex-1'

    // Remove button
    const removeBtn = document.createElement('button')
    removeBtn.type = 'button'
    removeBtn.className = this.removeClassesValue
    removeBtn.innerHTML = '×'
    removeBtn.style.fontSize = '16px'
    removeBtn.style.width = '18px'
    removeBtn.style.height = '18px'
    removeBtn.style.display = 'flex'
    removeBtn.style.alignItems = 'center'
    removeBtn.style.justifyContent = 'center'

    // Add click handler to remove button
    removeBtn.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.removeTag(value, tag)
    })

    tag.appendChild(tagText)
    tag.appendChild(removeBtn)

    this.tagContainer.appendChild(tag)
  }

  removeTag(value, tagElement) {
    // Remove the tag from DOM
    tagElement.remove()

    // Remove from TomSelect
    this.tomSelect.removeItem(value, false) // false = don't trigger events

    // Show the item back in dropdown
    this.handleItemRemove(value)
  }

  disconnect() {
    if (this.tomSelect) {
      this.tomSelect.destroy()
    }
  }

  processAndAddData(data) {
    const optgroups = data.map((group) => ({
      value: group.value,
      label: group.label,
    }));

    [...optgroups, ...Object.values(this.tomselect.optgroups)].forEach((optgroup) => {
      this.tomselect.addOptionGroup(optgroup.value, optgroup);
    });
  }
}
